import { getShadow } from '@/core/init'

/**
 * 深度克隆对象
 */
export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') {
    return obj
  }

  if (obj instanceof Date) {
    return new Date(obj.getTime()) as any
  }

  if (obj instanceof Array) {
    return obj.map((item) => deepClone(item)) as any
  }

  if (obj instanceof Object) {
    const clonedObj = {} as T
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key])
      }
    }
    return clonedObj
  }

  return obj
}

/**
 * 深度合并对象
 */
export function deepMerge<T extends Record<string, any>>(
  target: T,
  ...sources: Partial<T>[]
): T {
  if (!sources.length) return target

  const source = sources.shift()
  if (!source) return target

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) {
          Object.assign(target, { [key]: {} })
        }
        deepMerge(target[key], source[key] as any)
      } else {
        Object.assign(target, { [key]: source[key] })
      }
    }
  }

  return deepMerge(target, ...sources)
}

/**
 * 判断是否为对象
 */
export function isObject(item: any): boolean {
  return item && typeof item === 'object' && !Array.isArray(item)
}

/**
 * 判断是否为空对象
 */
export function isEmptyObject(obj: any): boolean {
  return isObject(obj) && Object.keys(obj).length === 0
}

/**
 * 防抖函数
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout> | null = null

  return function (this: any, ...args: Parameters<T>) {
    const context = this

    if (timeout) {
      clearTimeout(timeout)
    }

    timeout = setTimeout(() => {
      func.apply(context, args)
    }, wait)
  }
}

/**
 * 节流函数
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean = false

  return function (this: any, ...args: Parameters<T>) {
    const context = this

    if (!inThrottle) {
      func.apply(context, args)
      inThrottle = true
      setTimeout(() => {
        inThrottle = false
      }, limit)
    }
  }
}

/**
 * 生成唯一 ID
 */
export function generateId(prefix: string = 'id'): string {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

/**
 * 生成 UUID v4
 */
export function uuid(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/**
 * 安全的 JSON 解析
 */
export function safeJsonParse<T = any>(
  json: string,
  defaultValue: T | null = null
): T | null {
  try {
    return JSON.parse(json)
  } catch {
    return defaultValue
  }
}

/**
 * 安全的 JSON 字符串化
 */
export function safeJsonStringify(
  obj: any,
  space?: number,
  maxDepth: number = 10
): string {
  const seen = new WeakSet()
  let depth = 0

  return JSON.stringify(
    obj,
    (key, value) => {
      // 处理循环引用
      if (typeof value === 'object' && value !== null) {
        if (seen.has(value)) {
          return '[Circular]'
        }
        seen.add(value)
      }

      // 限制深度
      if (depth > maxDepth) {
        return '[Max Depth Reached]'
      }
      depth++

      // 过滤敏感信息
      if (
        typeof key === 'string' &&
        (key.toLowerCase().includes('password') ||
          key.toLowerCase().includes('token') ||
          key.toLowerCase().includes('secret'))
      ) {
        return '[REDACTED]'
      }

      return value
    },
    space
  )
}

/**
 * 获取对象深层属性
 */
export function getNestedProperty(
  obj: any,
  path: string,
  defaultValue: any = undefined
): any {
  const keys = path.split('.')
  let result = obj

  for (const key of keys) {
    if (result === null || result === undefined) {
      return defaultValue
    }
    result = result[key]
  }

  return result !== undefined ? result : defaultValue
}

/**
 * 设置对象深层属性
 */
export function setNestedProperty(obj: any, path: string, value: any): void {
  const keys = path.split('.')
  const lastKey = keys.pop()

  if (!lastKey) return

  let current = obj
  for (const key of keys) {
    if (!(key in current) || typeof current[key] !== 'object') {
      current[key] = {}
    }
    current = current[key]
  }

  current[lastKey] = value
}

/**
 * 格式化文件大小
 */
export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB']

  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

/**
 * 格式化时间差
 */
export function formatDuration(ms: number): string {
  if (ms < 1000) {
    return `${ms}ms`
  }

  const seconds = Math.floor(ms / 1000)
  if (seconds < 60) {
    return `${seconds}s`
  }

  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) {
    return `${minutes}m ${seconds % 60}s`
  }

  const hours = Math.floor(minutes / 60)
  return `${hours}h ${minutes % 60}m`
}

/**
 * 延迟执行
 */
export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * 重试函数
 */
export async function retry<T>(
  fn: () => Promise<T>,
  options: {
    retries?: number
    delay?: number
    backoff?: number
    onRetry?: (error: Error, attempt: number) => void
  } = {}
): Promise<T> {
  const { retries = 3, delay = 1000, backoff = 2, onRetry } = options

  let lastError: Error

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error as Error

      if (attempt < retries) {
        const waitTime = delay * Math.pow(backoff, attempt)
        onRetry?.(lastError, attempt + 1)
        await sleep(waitTime)
      }
    }
  }

  throw lastError!
}

/**
 * 截断字符串
 */
export function truncate(
  str: string,
  maxLength: number,
  suffix: string = '...'
): string {
  if (str.length <= maxLength) {
    return str
  }
  return str.slice(0, maxLength - suffix.length) + suffix
}

/**
 * 移除对象中的 undefined 和 null 值
 */
export function removeEmpty<T extends Record<string, any>>(obj: T): Partial<T> {
  const result: any = {}

  for (const key in obj) {
    if (obj[key] !== undefined && obj[key] !== null) {
      if (isObject(obj[key])) {
        result[key] = removeEmpty(obj[key])
      } else {
        result[key] = obj[key]
      }
    }
  }

  return result
}

/**
 * 数组去重
 */
export function unique<T>(arr: T[]): T[] {
  return Array.from(new Set(arr))
}

/**
 * 数组分组
 */
export function groupBy<T>(
  arr: T[],
  key: keyof T | ((item: T) => string)
): Record<string, T[]> {
  return arr.reduce((result, item) => {
    const groupKey = typeof key === 'function' ? key(item) : String(item[key])
    if (!result[groupKey]) {
      result[groupKey] = []
    }
    result[groupKey].push(item)
    return result
  }, {} as Record<string, T[]>)
}

/**
 * 获取浏览器信息
 */
export function getBrowserInfo(): {
  name: string
  version: string
  os: string
} {
  const ua = navigator.userAgent
  let browserName = 'Unknown'
  let browserVersion = 'Unknown'
  let osName = 'Unknown'

  // 检测浏览器
  if (ua.includes('Chrome')) {
    browserName = 'Chrome'
    browserVersion = ua.match(/Chrome\/(\d+)/)?.[1] || 'Unknown'
  } else if (ua.includes('Safari')) {
    browserName = 'Safari'
    browserVersion = ua.match(/Version\/(\d+)/)?.[1] || 'Unknown'
  } else if (ua.includes('Firefox')) {
    browserName = 'Firefox'
    browserVersion = ua.match(/Firefox\/(\d+)/)?.[1] || 'Unknown'
  } else if (ua.includes('Edge')) {
    browserName = 'Edge'
    browserVersion = ua.match(/Edge\/(\d+)/)?.[1] || 'Unknown'
  }

  // 检测操作系统
  if (ua.includes('Windows')) {
    osName = 'Windows'
  } else if (ua.includes('Mac')) {
    osName = 'macOS'
  } else if (ua.includes('Linux')) {
    osName = 'Linux'
  } else if (ua.includes('Android')) {
    osName = 'Android'
  } else if (ua.includes('iOS')) {
    osName = 'iOS'
  }

  return {
    name: browserName,
    version: browserVersion,
    os: osName
  }
}

/**
 * 检查是否为移动设备
 */
export function isMobile(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  )
}

/**
 * 获取查询参数
 */
export function getQueryParams(url?: string): Record<string, string> {
  const searchParams = new URLSearchParams(
    url ? new URL(url).search : window.location.search
  )
  const params: Record<string, string> = {}

  searchParams.forEach((value, key) => {
    params[key] = value
  })

  return params
}

/**
 * 设置查询参数
 */
export function setQueryParams(
  params: Record<string, string>,
  url?: string
): string {
  const targetUrl = url ? new URL(url) : new URL(window.location.href)

  Object.entries(params).forEach(([key, value]) => {
    targetUrl.searchParams.set(key, value)
  })

  return targetUrl.toString()
}

/**
 * 计算字符串哈希值
 */
export function hashCode(str: string): number {
  let hash = 0
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i)
    hash = (hash << 5) - hash + char
    hash = hash & hash // Convert to 32bit integer
  }
  return hash
}

/**
 * 脱敏处理
 */
export function maskSensitiveData(
  data: string,
  type: 'email' | 'phone' | 'idCard' | 'bankCard' = 'email'
): string {
  switch (type) {
    case 'email':
      return data.replace(/(.{2}).*(@.*)/, '$1***$2')
    case 'phone':
      return data.replace(/(\d{3})\d{4}(\d{4})/, '$1****$2')
    case 'idCard':
      return data.replace(/(\d{6})\d{8}(\d{4})/, '$1********$2')
    case 'bankCard':
      return data.replace(/(\d{4})\d+(\d{4})/, '$1 **** **** $2')
    default:
      return data
  }
}

/**
 * 复制到剪贴板
 */
export async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(text)
      return true
    } else {
      // 降级方案
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.style.position = 'fixed'
      textarea.style.opacity = '0'
      document.body.appendChild(textarea)
      textarea.select()
      const success = document.execCommand('copy')
      document.body.removeChild(textarea)
      return success
    }
  } catch {
    return false
  }
}

/**
 * 下载文件
 */
export function downloadFile(
  content: string,
  filename: string,
  mimeType: string = 'text/plain'
): void {
  const blob = new Blob([content], { type: mimeType })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// 全局缓存：URL -> Promise
const scriptLoadCache = new Map<string, Promise<HTMLScriptElement>>()

export function loadScript(
  url: string,
  options: {
    async?: boolean
    defer?: boolean
    id?: string
    attributes?: Record<string, string> // 修复类型：应为 Record 而非 [string, string]
  } = {}
): Promise<HTMLScriptElement> {
  // 如果已有加载任务，直接返回它的 Promise
  if (scriptLoadCache.has(url)) {
    return scriptLoadCache.get(url)!
  }

  const promise = new Promise<HTMLScriptElement>((resolve, reject) => {
    // 再次检查（防止极短时间内多次调用）
    const existingScript = document.querySelector<HTMLScriptElement>(
      `script[src="${CSS.escape(url)}"]`
    )
    if (existingScript) {
      // 检查是否已加载完成
      if (existingScript.hasAttribute('data-loaded')) {
        return resolve(existingScript)
      }

      // 如果脚本存在但未完成，监听其事件
      const handleLoad = () => {
        existingScript.setAttribute('data-loaded', 'true')
        resolve(existingScript)
      }
      const handleError = () => {
        // existingScript.setAttribute('data-error', 'true')
        scriptLoadCache.delete(url) // ✅ 删除缓存
        existingScript.remove()
        reject(new Error(`加载脚本失败: ${url}`))
      }

      existingScript.addEventListener('load', handleLoad, { once: true })
      existingScript.addEventListener('error', handleError, { once: true })
      return
    }

    // 创建新脚本
    const script = document.createElement('script')
    script.src = url
    script.async = options.async !== false
    script.defer = options.defer === true
    script.type = 'text/javascript'

    if (options.id) script.id = options.id
    if (options.attributes) {
      Object.entries(options.attributes).forEach(([key, value]) => {
        script.setAttribute(key, value)
      })
    }

    const handleLoad = () => {
      script.setAttribute('data-loaded', 'true')
      resolve(script)
    }
    const handleError = () => {
      script.setAttribute('data-error', 'true')
      reject(new Error(`加载脚本失败: ${url}`))
    }

    script.addEventListener('load', handleLoad, { once: true })
    script.addEventListener('error', handleError, { once: true })

    document.head.appendChild(script)
  })

  // 缓存 Promise
  scriptLoadCache.set(url, promise)

  // 清理缓存（可选）：成功或失败后不清除，以便后续快速返回结果
  // 如果希望失败后可重试，可在 reject 时 delete cache[url]

  return promise
}

export function loadCSS(href: string, id: string) {
  // 防止重复加载（通过 id 或 href 去重）
  if (id && document.getElementById(id)) {
    return Promise.resolve()
  }
  if (!id && document.querySelector(`link[href="${href}"]`)) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = href

    if (id) link.id = id

    link.onerror = () => reject(new Error(`Failed to load CSS: ${href}`))

    document.head.appendChild(link)

    // const shadow = getShadow()
    // if (shadow) shadow.appendChild(link)
  })
}

export function getCdnUrl(
  url: string,
  { version = false }: { version?: boolean } = {}
): string {
  const baseUrl = 'https://prod-rum-sentry.twyfordtile.net'
  // 确保 url 不以 / 开头，baseUrl 不以 / 结尾
  const cleanUrl = url.replace(/^\/+/, '') // 移除开头所有 /

  const segments = [
    baseUrl,
    version ? `sentry/${process.env.version}` : '', // 构建的sentry版本号
    cleanUrl
  ].filter((v) => !!v)
  return segments.join('/')
}

export function focusedBlur() {
  const focusedElement = document.activeElement as HTMLElement
  if (focusedElement) focusedElement.blur()
}

/**
 * 为 ESB 格式化语言
 * @param lang
 * @returns
 */
export function formatLanguage(lang: string): string {
  if (lang.includes('-')) {
    return lang.split('-')[0].toLowerCase()
  }
  return lang.toLowerCase()
}
